/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { LogoutOutlined } from '@ant-design/icons';
import { formatMustacheStringWithData } from '@kargotech/tms-common/Utilities/utils';
import { AuthContext } from '@kargotech/tms-core/auth';
import { MainLayout as MainLayoutTemplate } from '@kargotech/tms-ui/templates';
import React, {
  Fragment, useCallback, useContext, useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useFlag } from '@unleash/proxy-client-react';

import notificationCodeUrlMap from '~/configurations/notification-code-url-map.json';
import profileNavigations from '~/configurations/ProfileNavigations';
import topNavigations, { ITopNavigation } from '~/configurations/top-navigations';
import { AppContext } from '~/contexts/AppProvider';
import { NavigationContext } from '~/contexts/NavigationProvider';
import { ProfileContext } from '~/contexts/ProfileProvider';
import useInAppNotification from '~/hooks/inAppNotificationHooks';
import { INotificationItem } from '~/models/notifications';
import useAuthTranslation from '~/modules/auth/hooks/useNamespacedTranslation';
import { FEATURE_FLAG } from '~/configurations/constants';

const { Logo, Menu, MenuItem } = MainLayoutTemplate;

interface IMainLayoutProps {
  children: React.ReactNode;
}

interface ITabNavigationFormatter {
  linkProps?: Record<string, unknown>;
  path: string;
  title: string;
}

export default function MainLayout({ children }: IMainLayoutProps) {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  useAuthTranslation({ skipSetDefaultNamespace: true });
  const { t, i18n } = useTranslation();
  const { logo: appLogo, projectName } = useContext(AppContext);
  const { logout } = useContext(AuthContext);
  const ENABLE_TRANSPORTER_PIPELINE = useFlag(FEATURE_FLAG.TRANSPORTER_PIPELINE);
  const KC_LTMS_RECEIVABLE_UPI_MIGRATION = useFlag(FEATURE_FLAG.KC_LTMS_RECEIVABLE_UPI_MIGRATION);

  const { getSelectedCompany, isAuthorizedToAccess, profile } = useContext(ProfileContext);

  const {
    subtitle, tabNavigation, isTabNavigationSticky, title, toolbarSection,
  } = useContext(NavigationContext);

  const { accessTypes, ksuid: currentCompanyKsuid } = getSelectedCompany();

  // TODO: Uncomment this once we need to show notification
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const [
    notifications,
    isFetchingNotifications,
    loadMoreNotification,
    readNotificationByKey,
    readAllNotification,
  ] = useInAppNotification({
    companyKsuid: currentCompanyKsuid,
    language: i18n.language,
  });
  /* eslint-disable @typescript-eslint/no-unused-vars */

  const navigationFilter = useCallback((topNavigationItem: ITopNavigation) => {
    const { condition } = topNavigationItem || {} as ITopNavigation;

    if (typeof condition === 'undefined') {
      return true;
    }

    if (typeof condition === 'boolean') {
      return Boolean(condition);
    }

    // Filter by role if specified
    const isRoleValid = Object.propertyIsEnumerable.call(condition, 'role')
      ? isAuthorizedToAccess(condition.role || [])
      : true;

    // If no known condition, mark as no condition at all
    return isRoleValid;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessTypes, isAuthorizedToAccess]);

  const topMenus = useMemo(
    () => {
      const topNavList: ITopNavigation[] = topNavigations({
        ENABLE_TRANSPORTER_PIPELINE,
        UPI_MIGRATION: KC_LTMS_RECEIVABLE_UPI_MIGRATION,
      });
      return topNavList.filter(navigationFilter).map(({
        key, path, text, menuProps = {},
      }) => (
      // Currently, only support setting active prop on MenuItem
      // if `pathname` startwith the given path
        <Link key={path} to={path}>
          <MenuItem active={pathname.startsWith(`/${key}`)} {...menuProps}>{text}</MenuItem>
        </Link>
      ));
    },
    [navigationFilter, pathname, ENABLE_TRANSPORTER_PIPELINE, KC_LTMS_RECEIVABLE_UPI_MIGRATION],
  );

  const logo = useMemo(
    () => <Logo alt={projectName} src={appLogo} />,
    [appLogo, projectName],
  );

  /* eslint-disable arrow-body-style */
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const notificationFormatter = useCallback((message, notification) => {
    // Format custom notification message based on the notification code
    // For example:
    // if (['5-14', '5-24'].includes(notification.actionCode)) {
    //   return (
    //     <>
    //       <Typography>{message}</Typography>
    //       <Typography.Text color="primary">{t('confirm_your_availability')}</Typography.Text>
    //     </>
    //   );
    // }
    return message as string;
  }, []);
  /* eslint-enable arrow-body-style */
  /* eslint-enable @typescript-eslint/no-unused-vars */

  // TODO: Uncomment this once we need to show notification
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const notificationSelectHandler = useCallback(
    async (notification: INotificationItem) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { actionCode, key, payload } = notification || {};
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const rawTargetUrl = notificationCodeUrlMap[actionCode] as string;
      const finalUrl = formatMustacheStringWithData(rawTargetUrl, payload) || '';

      if (finalUrl) {
        navigate(finalUrl);
      }

      if (!notification.isRead && key) {
        readNotificationByKey(key);
      }
    },
    [navigate, readNotificationByKey],
  );

  const profileDropdownMenus = useMemo(
    () => (
      <Menu>
        {profileNavigations
          .filter(navigationFilter)
          .map(({ icon, path, text }) => (
            <Fragment key={path}>
              <Menu.Item
                key={path}
                {...(icon ? { icon } : {})}
                onClick={() => navigate(path)}
              >
                {text}
              </Menu.Item>
              <Menu.Divider />
            </Fragment>
          ))}
        <Menu.Item icon={<LogoutOutlined />} onClick={logout}>
          {t('auth:logout')}
        </Menu.Item>
      </Menu>
    ),
    [navigate, logout, navigationFilter, t],
  );

  const tabNavigationFormatter = useCallback(
    ({ path, title: tabTitle, linkProps }: ITabNavigationFormatter) => (
      <Link to={path} {...linkProps}>{tabTitle}</Link>
    ),
    [],
  );

  const tabNavigationSelectedKeys = useMemo(
    () => tabNavigation
      .map((tab) => tab.path)
      .filter((tabPath) => {
        const cleanTabPath = tabPath
          .split('/')
          .filter((subPath) => !subPath.startsWith(':'))
          .join('/');
          // search param usage for customer-detail page
          // which use search param to indentify active tab
        return pathname === tabPath || cleanTabPath === search;
      }),
    [pathname, search, tabNavigation],
  );

  return (
    <MainLayoutTemplate
      logo={logo}
      notification={undefined}
      profile={{
        company: profile.company.name,
        dropDownMenus: profileDropdownMenus,
        name: profile.name,
      }}
      subtitle={subtitle}
      tabNavigation={{
        list: tabNavigation,
        tabFormatter: tabNavigationFormatter,
        selectedKeys: tabNavigationSelectedKeys,
        isSticky: isTabNavigationSticky,
      }}
      title={title}
      toolbar={toolbarSection}
      topMenus={topMenus}
    >
      {children}
    </MainLayoutTemplate>
  );
}
